import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Shield, RefreshCw, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface EmailVerificationProps {
  email: string;
  userId: string;
  onBack: () => void;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({ email, userId, onBack }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resending, setResending] = useState(false);
  const { verifyEmail, resendVerificationCode } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || verified) return;
    setError('');
    setLoading(true);

    try {
      await verifyEmail(userId, verificationCode);
      setVerified(true);
      // HomeRoute redirects to the correct dashboard based on the authenticated user
      navigate('/', { replace: true });
    } catch (error: any) {
      setError(error.response?.data?.message || 'Verification failed. Please try again.');
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setResending(true);

    try {
      await resendVerificationCode(userId);
      setError(''); // Clear any existing errors
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to resend verification code.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-900">
      <div className="p-8 bg-gray-800 rounded-lg shadow-md w-96">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-blue-400">
            Verify Your Email
          </h2>
          <p className="text-gray-300 mt-2">
            We've sent a 6-digit verification code to:
          </p>
          <p className="text-blue-400 font-semibold">{email}</p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-600/20 border border-red-500 rounded-md text-sm text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2" htmlFor="verificationCode">
              Verification Code
            </label>
            <div className="relative">
              <input
                type="text"
                id="verificationCode"
                className="w-full p-3 pl-10 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:border-blue-500 text-center text-lg tracking-widest"
                placeholder="123456"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                required
              />
              <Mail className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || verified || verificationCode.length !== 6}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 mb-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {verified ? 'Verified! Redirecting...' : loading ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <div className="text-center">
          <p className="text-gray-400 text-sm mb-4">
            Didn't receive the code?
          </p>
          <button
            onClick={handleResendCode}
            disabled={resending}
            className="text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center justify-center mx-auto mb-4 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${resending ? 'animate-spin' : ''}`} />
            {resending ? 'Sending...' : 'Resend Code'}
          </button>
          
          <button
            onClick={onBack}
            className="text-gray-400 hover:text-gray-300 text-sm font-medium flex items-center justify-center mx-auto"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Registration
          </button>
        </div>

        <div className="mt-6 text-center text-xs text-gray-500">
          <p>The verification code expires in 15 minutes</p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification; 